import { Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { ErrorBoundary } from '@/components/ErrorBoundary';

// Lazy load page components for better performance
const Landing = lazy(() => import('@/pages/Landing').then(module => ({ default: module.Landing })));
const MainLanding = lazy(() => import('@/pages/MainLanding').then(module => ({ default: module.MainLanding })));
const SignIn = lazy(() => import('@/pages/SignIn').then(module => ({ default: module.SignIn })));
const SignUp = lazy(() => import('@/pages/SignUp').then(module => ({ default: module.SignUp })));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword').then(module => ({ default: module.ForgotPassword })));
const Dashboard = lazy(() => import('@/pages/Dashboard').then(module => ({ default: module.Dashboard })));
const Courses = lazy(() => import('@/pages/Courses').then(module => ({ default: module.Courses })));
const Checkout = lazy(() => import('@/pages/Checkout').then(module => ({ default: module.Checkout })));
const LessonViewer = lazy(() => import('@/pages/LessonViewer').then(module => ({ default: module.LessonViewer })));
const DemoLesson = lazy(() => import('@/pages/DemoLesson').then(module => ({ default: module.DemoLesson })));
const Feedback = lazy(() => import('@/pages/Feedback').then(module => ({ default: module.Feedback })));
const Terms = lazy(() => import('@/pages/Terms').then(module => ({ default: module.Terms })));
const About = lazy(() => import('@/pages/About').then(module => ({ default: module.About })));
const Privacy = lazy(() => import('@/pages/Privacy').then(module => ({ default: module.Privacy })));
const Preferences = lazy(() => import('@/pages/Preferences').then(module => ({ default: module.Preferences })));
const InstructorRegister = lazy(() => import('@/pages/InstructorRegister').then(module => ({ default: module.InstructorRegister })));
const InstructorDashboard = lazy(() => import('@/pages/InstructorDashboard').then(module => ({ default: module.InstructorDashboard })));
const AdminInstructors = lazy(() => import('@/pages/AdminInstructors').then(module => ({ default: module.AdminInstructors })));
const AdminDashboard = lazy(() => import('@/pages/AdminDashboard').then(module => ({ default: module.AdminDashboard })));
const AdminPromoCodes = lazy(() => import('@/pages/AdminPromoCodes').then(module => ({ default: module.AdminPromoCodes })));
const AdminUsers = lazy(() => import('@/pages/AdminUsers').then(module => ({ default: module.AdminUsers })));
const AdminContent = lazy(() => import('@/pages/AdminContent').then(module => ({ default: module.AdminContent })));
const AdminEmail = lazy(() => import('@/pages/AdminEmail').then(module => ({ default: module.AdminEmail })));
const AdminAnalytics = lazy(() => import('@/pages/AdminAnalytics').then(module => ({ default: module.AdminAnalytics })));
const ProgressAnalytics = lazy(() => import('@/pages/ProgressAnalytics').then(module => ({ default: module.ProgressAnalytics })));
const NotFound = lazy(() => import('@/pages/NotFound').then(module => ({ default: module.NotFound })));

// Loading component
function LoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--cld-color-off-white)' }}>
      <div className="text-center">
        <div
          className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent"
          style={{ borderColor: 'var(--cld-color-secondary)', borderTopColor: 'transparent' }}
        ></div>
        <p className="mt-4" style={{ color: 'var(--cld-color-charcoal)' }}>Loading...</p>
      </div>
    </div>
  );
}

// Removed old HomePage and AboutPage - now using Landing page

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
      <AuthProvider>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/home" element={<MainLanding />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/about" element={<About />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/instructor/register" element={<InstructorRegister />} />
            <Route
              path="/instructor/dashboard"
              element={
                <ProtectedRoute>
                  <InstructorDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/instructors"
              element={
                <ProtectedRoute>
                  <AdminInstructors />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/promo-codes"
              element={
                <ProtectedRoute>
                  <AdminPromoCodes />
                </ProtectedRoute>
              }
            />
            <Route path="/demo/:lessonId" element={<DemoLesson />} />
            <Route
              path="/courses"
              element={
                <ProtectedRoute>
                  <Courses />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lesson/:lessonId"
              element={
                <ProtectedRoute>
                  <LessonViewer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/feedback"
              element={
                <ProtectedRoute>
                  <Feedback />
                </ProtectedRoute>
              }
            />
            <Route
              path="/checkout"
              element={
                <ProtectedRoute>
                  <Checkout />
                </ProtectedRoute>
              }
            />
            <Route
              path="/checkout/:courseId"
              element={
                <ProtectedRoute>
                  <Checkout />
                </ProtectedRoute>
              }
            />
            <Route
              path="/progress"
              element={
                <ProtectedRoute>
                  <ProgressAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/preferences"
              element={
                <ProtectedRoute>
                  <Preferences />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute>
                  <AdminUsers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/content"
              element={
                <ProtectedRoute>
                  <AdminContent />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/email"
              element={
                <ProtectedRoute>
                  <AdminEmail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/analytics"
              element={
                <ProtectedRoute>
                  <AdminAnalytics />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
